import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, TrendingUp } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { goToApp } from "@/lib/appUrl";

const LINKS = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#how" },
  { label: "Pricing", href: "#pricing" },
  { label: "Voices", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#020617]/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 group"
        >
          <LogoMark className="text-white group-hover:scale-110 transition-transform" />
          <span className="font-display text-xl font-semibold tracking-tight text-white">
            Noizbloc
          </span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="text-sm text-slate-400 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/trending"
            data-testid="nav-trending"
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <TrendingUp size={13} className="text-red-400" /> Now Playing
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-login-btn"
            onClick={() => { if (!goToApp("/login")) navigate("/login"); }}
            className="hidden sm:inline-flex items-center text-sm font-medium text-slate-300 hover:text-white px-3 py-2.5 transition-colors"
          >
            Sign in
          </button>
          <button
            data-testid="nav-register-btn"
            onClick={() => { if (!goToApp("/register")) navigate("/register"); }}
            className="hidden sm:inline-flex items-center rounded-full bg-white text-black text-sm font-medium px-5 py-2.5 hover:bg-slate-200 active:scale-[0.98] transition-all"
          >
            Get started
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#020617]/95 backdrop-blur-xl border-b border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-${l.href.slice(1)}`}
                  onClick={() => go(l.href)}
                  className="text-left text-base text-slate-300 hover:text-white transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/trending"
                data-testid="nav-mobile-trending"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 text-left text-base text-slate-300 hover:text-white transition-colors"
              >
                <TrendingUp size={14} className="text-red-400" /> Now Playing
              </Link>
              <button
                data-testid="nav-mobile-login-btn"
                onClick={() => { if (!goToApp("/login")) navigate("/login"); }}
                className="mt-2 inline-flex items-center justify-center rounded-full border border-white/15 text-white text-sm font-medium px-5 py-3"
              >
                Sign in
              </button>
              <button
                data-testid="nav-mobile-register-btn"
                onClick={() => { if (!goToApp("/register")) navigate("/register"); }}
                className="inline-flex items-center justify-center rounded-full bg-white text-black text-sm font-medium px-5 py-3"
              >
                Get started
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
